// src/store/slice/mainSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/* ===== 타입들 (api/main.ts에서 export한 타입과 동일하게 맞추세요) ===== */
export type SectorTab = { key: string; label: string; order: number };
export type LatestDigest = {
    savedCount: number | null;
    minFeedId: number | null;
    maxFeedId: number | null;
    sectorCounts: Record<string, number>;
    generatedAtKST: string;
};
export type FeatureMetricItem = { title: string; desc: string; value: number };
export type FeatureMetricGroup = { groupTitle: string; items: FeatureMetricItem[] };
export type ReactionProfileDTO = {
    windows: number[];
    weights: Record<number, number>;
    decay: Record<number, number>;
};
export type ReactionMetrics = {
    blendedAlpha: number | null;
    profileUsed: Record<string, any> | null;
    meta: Record<string, any> | null;
};
export type SignalCard = {
    feedId: number;
    title: string | null;
    url: string | null;
    sector: string;
    publishedAt: string | null; // "YYYY-MM-DD HH:mm"
    impactScore: number | null;
    interpretationKr: string | null;
    reaction: ReactionMetrics | null;
};
export type WindowStat = {
    retWeighted: number | null;
    retBaseline: number | null;
    alpha: number | null;
    sample: number;
    confidence: string;
};
export type ReactionWindowsDTO = {
    windows: Record<number, WindowStat>;
    blendedAlpha: number | null;
    profileUsed: ReactionProfileDTO;
    meta: Record<string, any>;
};
export type InsightRow = {
    feedId: number;
    title: string | null;
    sector: string | null;
    publishedAt: string | null;
    impactScore: number | null;
    sourceName: string | null;
};

/* ===== 상태 정의 ===== */
export type MainState = {
    /** 섹터 탭 */
    sectorTabs: SectorTab[];
    loadingSectorTabs: boolean;
    sectorTabsError?: string | null;

    /** 오늘 요약 */
    latestDigest: LatestDigest | null;
    loadingLatestDigest: boolean;

    /** 기능 메트릭 */
    featureMetrics: FeatureMetricGroup[];
    loadingFeatureMetrics: boolean;

    /** 섹터별 시그널 캐시 및 로딩/오류 */
    sectorSignals: Record<string, SignalCard[]>;
    sectorSignalsLoading: Record<string, boolean>;
    sectorSignalsPaging: Record<string, { limit: number; offset: number }>;
    sectorSignalsError?: Record<string, string | null>;

    /** 섹터별 반응 프로파일 캐시/로딩/오류 */
    reactionProfileBySector: Record<string, ReactionProfileDTO>;
    reactionProfileLoading: Record<string, boolean>;
    reactionProfileError?: Record<string, string | null>;

    /** feedId별 window 성과 캐시/로딩/오류 */
    reactionWindowsByFeedId: Record<string, ReactionWindowsDTO>;
    reactionWindowsLoading: Record<string, boolean>;
    reactionWindowsError?: Record<string, string | null>;

    /** 인사이트 리스트 */
    insightList: InsightRow[];
    loadingInsightList: boolean;
    insightListPaging: { limit: number; offset: number };
    insightListError?: string | null;

    /** 마지막 에러(옵셔널, 레거시) */
    error?: string | null;
};

const initialState: MainState = {
    sectorTabs: [],
    loadingSectorTabs: false,
    sectorTabsError: null,

    latestDigest: null,
    loadingLatestDigest: false,

    featureMetrics: [],
    loadingFeatureMetrics: false,

    sectorSignals: {},
    sectorSignalsLoading: {},
    sectorSignalsPaging: {},
    sectorSignalsError: {},

    reactionProfileBySector: {},
    reactionProfileLoading: {},
    reactionProfileError: {},

    reactionWindowsByFeedId: {},
    reactionWindowsLoading: {},
    reactionWindowsError: {},

    insightList: [],
    loadingInsightList: false,
    insightListPaging: { limit: 20, offset: 0 },
    insightListError: null,

    error: null,
};

/* ===== Slice ===== */
const mainSlice = createSlice({
    name: 'main',
    initialState,
    reducers: {
        /* ---- 섹터 탭 ---- */
        fetchSectorTabsRequested(state) {
            state.loadingSectorTabs = true;
            state.sectorTabsError = null;
            state.error = null;
        },
        fetchSectorTabsSucceeded(state, action: PayloadAction<any>) {
            state.loadingSectorTabs = false;
            const p = action.payload;
            const list = Array.isArray(p) ? p
                            : Array.isArray(p?.items) ? p.items
                            : Array.isArray(p?.rows) ? p.rows
                            : [];
            state.sectorTabs = list as SectorTab[];
        },
        fetchSectorTabsFailed(state, action: PayloadAction<string>) {
            state.loadingSectorTabs = false;
            state.sectorTabsError = action.payload;
            state.error = action.payload;
        },

        /* ---- 오늘 요약 ---- */
        fetchLatestDigestRequested(state) {
            state.loadingLatestDigest = true;
            state.error = null;
        },
        fetchLatestDigestSucceeded(state, action: PayloadAction<LatestDigest>) {
            state.loadingLatestDigest = false;
            state.latestDigest = action.payload || null;
        },
        fetchLatestDigestFailed(state, action: PayloadAction<string>) {
            state.loadingLatestDigest = false;
            state.error = action.payload;
        },

        /* ---- 기능 메트릭 ---- */
        fetchFeatureMetricsRequested(state) {
            state.loadingFeatureMetrics = true;
            state.error = null;
        },
        fetchFeatureMetricsSucceeded(state, action: PayloadAction<FeatureMetricGroup[]>) {
            state.loadingFeatureMetrics = false;
            state.featureMetrics = action.payload || [];
        },
        fetchFeatureMetricsFailed(state, action: PayloadAction<string>) {
            state.loadingFeatureMetrics = false;
            state.error = action.payload;
        },

        /* ---- 섹터별 시그널 ---- */
        fetchSectorSignalsRequested(
            state,
            action: PayloadAction<{ sector: string; limit?: number; offset?: number }>
        ) {
            const { sector } = action.payload;
            state.sectorSignalsLoading[sector] = true;
            // 개별 섹터 에러 초기화
            if (!state.sectorSignalsError) state.sectorSignalsError = {};
            state.sectorSignalsError[sector] = null;

            state.error = null;
            if (!state.sectorSignalsPaging[sector]) {
                state.sectorSignalsPaging[sector] = {
                    limit: action.payload.limit ?? 10,
                    offset: action.payload.offset ?? 0,
                };
            }
        },
        fetchSectorSignalsSucceeded(
            state,
            action: PayloadAction<any>
        ) {
            const p = action.payload || {};
            const sector = p.sector ?? p.sectorKey ?? p.key;
            if(!sector) return;
            const list = Array.isArray(p.items) ? p.items
                : Array.isArray(p.rows) ? p.rows
                : Array.isArray(p) ? p
                : [];
            const limit = Number.isFinite(p.limit) ? p.limit
                : state.sectorSignalsPaging[sector]?.limit ?? (list?.length ?? 10);
            const offset = Number.isFinite(p.offset) ? p.offset
                : state.sectorSignalsPaging[sector]?.offset ?? 0;

            state.sectorSignalsLoading[sector] = false;
            state.sectorSignals[sector] = (list || []) as SignalCard[];
            state.sectorSignalsPaging[sector] = {limit, offset};
        },
        fetchSectorSignalsFailed(
            state,
            action: PayloadAction<{ sector: string; message: string }>
        ) {
            const { sector, message } = action.payload;
            state.sectorSignalsLoading[sector] = false;
            if (!state.sectorSignalsError) state.sectorSignalsError = {};
            state.sectorSignalsError[sector] = message || '로드 실패';
            state.error = message;
        },

        /* ---- 섹터 반응 프로파일 ---- */
        fetchReactionProfileRequested(state, action: PayloadAction<{ sector: string }>) {
            const { sector } = action.payload;
            state.reactionProfileLoading[sector] = true;
            if (!state.reactionProfileError) state.reactionProfileError = {};
            state.reactionProfileError[sector] = null;
            state.error = null;
        },
        fetchReactionProfileSucceeded(
            state,
            action: PayloadAction<{ sector: string; profile: ReactionProfileDTO }>
        ) {
            const { sector, profile } = action.payload;
            state.reactionProfileLoading[sector] = false;
            state.reactionProfileBySector[sector] = profile;
        },
        fetchReactionProfileFailed(
            state,
            action: PayloadAction<{ sector: string; message: string }>
        ) {
            const { sector, message } = action.payload;
            state.reactionProfileLoading[sector] = false;
            if (!state.reactionProfileError) state.reactionProfileError = {};
            state.reactionProfileError[sector] = message || '로드 실패';
            state.error = message;
        },

        /* ---- feed per-window 성과 ---- */
        fetchReactionWindowsRequested(state, action: PayloadAction<{ feedId: number }>) {
            const k = String(action.payload.feedId);
            state.reactionWindowsLoading[k] = true;
            if (!state.reactionWindowsError) state.reactionWindowsError = {};
            state.reactionWindowsError[k] = null;
            state.error = null;
        },
        fetchReactionWindowsSucceeded(
            state,
            action: PayloadAction<{ feedId: number; data: ReactionWindowsDTO }>
        ) {
            const k = String(action.payload.feedId);
            state.reactionWindowsLoading[k] = false;
            state.reactionWindowsByFeedId[k] = action.payload.data;
        },
        fetchReactionWindowsFailed(
            state,
            action: PayloadAction<{ feedId: number; message: string }>
        ) {
            const k = String(action.payload.feedId);
            state.reactionWindowsLoading[k] = false;
            if (!state.reactionWindowsError) state.reactionWindowsError = {};
            state.reactionWindowsError[k] = action.payload.message || '로드 실패';
            state.error = action.payload.message;
        },

        /* ---- 인사이트 리스트 ---- */
        fetchInsightListRequested(
            state,
            action: PayloadAction<{ limit?: number; offset?: number }>
        ) {
            state.loadingInsightList = true;
            state.insightListError = null;
            state.error = null;

            if (typeof action.payload.limit === 'number' || typeof action.payload.offset === 'number') {
                state.insightListPaging = {
                    limit: action.payload.limit ?? state.insightListPaging.limit,
                    offset: action.payload.offset ?? state.insightListPaging.offset,
                };
            }
        },
        fetchInsightListSucceeded(
            state,
            action: PayloadAction<any>
        ) {
            state.loadingInsightList = false;
            const p = action.payload;
            const items = Array.isArray(p) ? p
                : Array.isArray(p?.items) ? p.items
                : Array.isArray(p?.rows) ? p.rows
                : [];
            const limit = Number.isFinite(p?.limit) ? p.limit : state.insightListPaging.limit;
            const offset = Number.isFinite(p?.offset) ? p.offset : state.insightListPaging.offset;

            state.insightList = items as InsightRow[];
            state.insightListPaging = {limit,offset};
        },
        fetchInsightListFailed(state, action: PayloadAction<string>) {
            state.loadingInsightList = false;
            state.insightListError = action.payload;
            state.error = action.payload;
        },
    },
});

export const {
    fetchSectorTabsRequested,
    fetchSectorTabsSucceeded,
    fetchSectorTabsFailed,

    fetchLatestDigestRequested,
    fetchLatestDigestSucceeded,
    fetchLatestDigestFailed,

    fetchFeatureMetricsRequested,
    fetchFeatureMetricsSucceeded,
    fetchFeatureMetricsFailed,

    fetchSectorSignalsRequested,
    fetchSectorSignalsSucceeded,
    fetchSectorSignalsFailed,

    fetchReactionProfileRequested,
    fetchReactionProfileSucceeded,
    fetchReactionProfileFailed,

    fetchReactionWindowsRequested,
    fetchReactionWindowsSucceeded,
    fetchReactionWindowsFailed,

    fetchInsightListRequested,
    fetchInsightListSucceeded,
    fetchInsightListFailed,
} = mainSlice.actions;

export default mainSlice.reducer;
